/**
 * Custom (non-preset) value parsing and validation for `choice_or_custom` /
 * `always_custom` catalogue fields — the client-side half of design doc
 * "2a"/"2c" (`docs/features/product-catalogue/`).
 *
 * Mirrors `catalogue_module/actions/_custom_value.py`'s parse/validate split
 * closely enough that a value this module calls "valid" is a value the
 * backend's `cost_configuration` will actually accept — the whole point of
 * validating locally is to never spend a ~6s Tharstern round trip on a value
 * already known to fail its field's constraints (design doc "2c": "Validate
 * locally before calling").
 *
 * Three-way result rather than a boolean: a control mid-entry (one of two
 * dimension inputs filled, say) is not wrong, just not finished yet, and the
 * two must render differently — "incomplete" shows no error and fires no
 * request; "invalid" shows the constraint message and also fires no request.
 */

import type { CatalogueField } from "../data/catalogue-types";
import {
  CUSTOM_OPTION_SENTINEL,
  type CatalogueConfigurationState,
  buildCatalogueConfiguration,
} from "./catalogue-configuration";

/** A count (WP9's `Versions`): an `always_custom` integer with a minimum.
 *  It starts at its minimum, needs no setting up, and never changes the
 *  variant, so a count is never live-costed (mirrors the backend's
 *  `dictionary_module/models/count_field.py`). */
export function isCountField(
  field: Pick<CatalogueField, "mode" | "customValueType" | "min">,
): boolean {
  return (
    field.mode === "always_custom" &&
    field.customValueType === "integer" &&
    field.min != null
  );
}

export type CustomValueValidation =
  | { status: "incomplete" }
  | { status: "invalid"; message: string }
  | { status: "valid"; value: string };

export function validateCustomIntegerValue(
  raw: string,
  field: Pick<CatalogueField, "label" | "step" | "min" | "max">,
): CustomValueValidation {
  const trimmed = raw.trim();
  if (!trimmed) return { status: "incomplete" };
  if (!/^-?\d+$/.test(trimmed)) {
    return {
      status: "invalid",
      message: `${field.label} must be a whole number`,
    };
  }
  const value = Number(trimmed);
  if (field.min != null && value < field.min) {
    return {
      status: "invalid",
      message: `${field.label} ${value} is below the minimum of ${field.min}`,
    };
  }
  if (field.max != null && value > field.max) {
    return {
      status: "invalid",
      message: `${field.label} ${value} exceeds the maximum of ${field.max}`,
    };
  }
  if (field.step != null && field.step > 0 && value % field.step !== 0) {
    return {
      status: "invalid",
      message: `${field.label} ${value} must be a multiple of ${field.step}`,
    };
  }
  return { status: "valid", value: String(value) };
}

/**
 * Canonical wire encoding for a "dimensions" custom value: `"{w} x {h}"`,
 * matching the backend's `_DIMENSIONS_RE` exactly (digits, optional
 * whitespace, `x`/`X`/`×`, optional whitespace, digits — nothing else). The
 * page's older always_custom dimension control used to append `" mm"`,
 * which that anchored regex does not accept, so a large-format dimension
 * would parse on this side and then fail as "invalid_custom_value" on the
 * backend for a reason invisible on screen. Units belong in the label the
 * control renders beside the inputs, never in the stored value.
 */
export function formatDimensionsValue(width: number, height: number): string {
  return `${width} x ${height}`;
}

export function validateCustomDimensionsValue(
  input: { width: string; height: string },
  field: Pick<CatalogueField, "label" | "customConstraints">,
): CustomValueValidation {
  const w = input.width.trim();
  const h = input.height.trim();
  if (!w || !h) return { status: "incomplete" };
  const width = Number(w);
  const height = Number(h);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    return {
      status: "invalid",
      message: `${field.label} dimensions must be numbers`,
    };
  }
  if (width <= 0 || height <= 0) {
    return {
      status: "invalid",
      message: `${field.label} dimensions must both be positive`,
    };
  }
  const constraints = field.customConstraints ?? {};
  for (const [axis, size] of [
    ["width", width],
    ["depth", height],
  ] as const) {
    const minV = constraints[`min_${axis}_mm`];
    const maxV = constraints[`max_${axis}_mm`];
    if (minV != null && size < minV) {
      return {
        status: "invalid",
        message: `${field.label} ${axis} ${size}mm is below the minimum of ${minV}mm`,
      };
    }
    if (maxV != null && size > maxV) {
      return {
        status: "invalid",
        message: `${field.label} ${axis} ${size}mm exceeds the maximum of ${maxV}mm`,
      };
    }
  }
  return { status: "valid", value: formatDimensionsValue(width, height) };
}

export function validateCustomTextValue(
  raw: string,
  field: Pick<CatalogueField, "label" | "customConstraints">,
): CustomValueValidation {
  const trimmed = raw.trim();
  if (!trimmed) return { status: "incomplete" };
  const maxLength = field.customConstraints?.max_length;
  if (maxLength != null && trimmed.length > maxLength) {
    return {
      status: "invalid",
      message: `${field.label} exceeds the maximum length of ${maxLength} characters`,
    };
  }
  if (field.customConstraints?.digits_only && !/^[0-9]+$/.test(trimmed)) {
    return {
      status: "invalid",
      message: `${field.label} must be digits only, for example 095751.`,
    };
  }
  return { status: "valid", value: trimmed };
}

/** Whether `field`'s current value in `state` is an *active* custom value —
 *  the "Other…" chip picked (choice_or_custom) or any non-empty entry
 *  (always_custom) — as opposed to a preset selection. Doesn't say whether
 *  it's valid, only whether it's the kind of value this module should be
 *  asked to validate at all. */
export function isActiveCustomValue(
  field: Pick<CatalogueField, "key" | "mode" | "customValueType" | "min">,
  state: Pick<CatalogueConfigurationState, "values" | "custom">,
): boolean {
  if (field.customValueType == null) return false;
  // A count never changes the variant, so there is nothing to live-cost.
  if (isCountField(field)) return false;
  if (field.mode === "always_custom") {
    return Boolean(state.custom[field.key]?.trim());
  }
  if (field.mode === "choice_or_custom") {
    return state.values[field.key] === CUSTOM_OPTION_SENTINEL;
  }
  return false;
}

/** For a `dimensions` field, the raw width/height typed so far — parsed from
 *  the DimensionControl's own `"{w} x {h}"` working value, or a pair of
 *  standalone width/height strings if the caller keeps them separately. */
export function parseDimensionsWorkingValue(raw: string): {
  width: string;
  height: string;
} {
  const m = raw.match(/(-?\d*\.?\d*)\s*[×x]\s*(-?\d*\.?\d*)/i);
  return { width: m?.[1] ?? "", height: m?.[2] ?? "" };
}

/** Dispatch to the field's own custom-value-type validator. */
export function validateCustomValue(
  field: Pick<
    CatalogueField,
    "label" | "customValueType" | "step" | "min" | "max" | "customConstraints"
  >,
  raw: string,
): CustomValueValidation {
  if (field.customValueType === "integer") {
    return validateCustomIntegerValue(raw, field);
  }
  if (field.customValueType === "dimensions") {
    return validateCustomDimensionsValue(
      parseDimensionsWorkingValue(raw),
      field,
    );
  }
  if (field.customValueType === "text") {
    return validateCustomTextValue(raw, field);
  }
  return { status: "incomplete" };
}

export interface CustomValueLadderResolution {
  /** The full built configuration, with every active custom field's raw
   *  entry replaced by its canonical validated value — ready to price — or
   *  `null` when nothing needs pricing yet (no active custom value) or
   *  can't yet (one is incomplete or invalid). */
  configuration: Record<string, string> | null;
  /** Field-tagged messages for any active custom value that fails its
   *  constraints, keyed by field key, for the page to render beside the
   *  offending control. Never populated for "incomplete" — that's normal
   *  mid-entry state, not an error. */
  fieldErrors: Record<string, string>;
}

/**
 * The single place a configuration's active custom values are validated and
 * folded into the shape the ladder-costing hook and `cost-configuration`
 * both consume. Presets need none of this — `buildCatalogueConfiguration`
 * alone is already a request-ready, instantly priceable configuration.
 */
export function resolveCustomValueLadderConfiguration(
  fields: CatalogueField[],
  state: CatalogueConfigurationState,
): CustomValueLadderResolution {
  const fieldErrors: Record<string, string> = {};
  let hasActiveCustom = false;
  let allResolved = true;
  const resolvedValues: Record<string, string> = {};

  for (const field of fields) {
    if (!isActiveCustomValue(field, state)) continue;
    hasActiveCustom = true;
    const raw = state.custom[field.key] ?? "";
    const result = validateCustomValue(field, raw);
    if (result.status === "invalid") {
      fieldErrors[field.key] = result.message;
      allResolved = false;
    } else if (result.status === "incomplete") {
      allResolved = false;
    } else {
      resolvedValues[field.key] = result.value;
    }
  }

  if (!hasActiveCustom || !allResolved) {
    return { configuration: null, fieldErrors };
  }

  const configuration = {
    ...buildCatalogueConfiguration(state),
    ...resolvedValues,
  };
  return { configuration, fieldErrors };
}
